"use client";

// src/divider.tsx
import * as React from "react";
import { Base, systemForwardRef } from "@stepstone-genesis/system";
import { FlexItem } from "@stepstone-genesis/components-flex";

// src/styles.ts
var root = {
  flex: "0 1 auto"
};
var dividerContainer = (props) => {
  var _a, _b;
  return {
    common: {
      display: "flex",
      flexWrap: "nowrap",
      justifyItems: "center",
      justifyContent: "space-between",
      alignItems: "center"
    },
    horizontal: {
      paddingY: (_a = props.spacing) != null ? _a : "spaceNone",
      paddingX: "spaceNone",
      width: "100%",
      flexDirection: "row"
    },
    vertical: {
      flexWrap: "nowrap",
      paddingX: (_b = props.spacing) != null ? _b : "spaceNone",
      paddingY: "spaceNone",
      height: "100%",
      flexDirection: "column"
    }
  };
};
var textStyles = (props) => {
  var _a;
  return {
    width: "auto",
    paddingX: "spaceS",
    display: "flex",
    color: (_a = props.color) != null ? _a : "colorTextSecondaryDefault",
    paddingY: "spaceNone",
    transform: "translateY(-50%)"
  };
};
var dividerCommonStyles = (color = "colorBorderDefault") => ({
  border: "none",
  backgroundColor: color
});
var dividers = {
  horizontal: {
    height: "1px",
    width: "100%"
  },
  vertical: {
    width: "1px",
    height: "100%"
  }
};
var styles = {
  root,
  dividers,
  dividerCommonStyles,
  dividerContainer,
  textStyles
};

// src/divider.tsx
import { Text } from "@stepstone-genesis/components-text";
var htmlElement = {
  horizontal: "hr",
  vertical: "div",
  withChildren: "span"
};
var Divider = systemForwardRef(function Divider2({
  as,
  isInverted,
  orientation = "horizontal",
  children,
  color,
  spacing,
  sx,
  ...rest
}, ref) {
  const dividerStyles = React.useMemo(
    () => ({
      ...styles.root,
      ...styles.dividerCommonStyles(color),
      ...styles.dividers[orientation],
      ...isInverted && { backgroundColor: "colorWhite" },
      ...sx
    }),
    [color, orientation, isInverted, sx]
  );
  const textStyles2 = React.useMemo(
    () => ({
      ...styles.textStyles({
        color,
        spacing
      }),
      ...isInverted && { color: "colorWhite" }
    }),
    [color, spacing, isInverted]
  );
  const containerStyles = React.useMemo(
    () => Object.assign(
      {},
      styles.dividerContainer({
        color,
        spacing
      })[orientation],
      styles.dividerContainer({
        color,
        spacing
      })["common"]
    ),
    [color, spacing, orientation]
  );
  return /* @__PURE__ */ React.createElement(
    Base,
    {
      as: "div",
      __themeDefinition: "divider",
      __root: containerStyles,
      "aria-orientation": orientation === "vertical" ? "vertical" : void 0,
      role: children || orientation === "vertical" ? "separator" : void 0,
      ...rest
    },
    /* @__PURE__ */ React.createElement(
      Base,
      {
        as: as || (children ? htmlElement.withChildren : htmlElement[orientation]),
        ref,
        __root: dividerStyles
      }
    ),
    children && orientation === "horizontal" && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
      FlexItem,
      {
        flxShrink: 0,
        flxGrow: 0,
        flxBasis: "auto",
        sx: { maxHeight: orientation === "horizontal" ? "1px" : "auto" }
      },
      /* @__PURE__ */ React.createElement(Text, { sx: textStyles2, size: "small" }, children)
    ), /* @__PURE__ */ React.createElement(
      Base,
      {
        as: as || htmlElement.withChildren,
        ref,
        __themeDefinition: "divider",
        __root: dividerStyles
      }
    ))
  );
});
export {
  Divider
};
